/// <amd-module name="Wit/Search/InstantSearchProviders/WorkItemSuggestionsProvider" />
import * as React from "react";
import { ITfsPageService, IProjectData } from "Tfs/Platform/Page";
import { announce } from "VSS/Core/Util/Accessibility";
import { IReadonlyObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext, Services } from "VSS/Platform/Context";
import { IVssScenario } from "VSS/Platform/Performance";
import { IMenuItem, IMenuItemDetails, MenuCell, MenuItemType } from "VSSUI/Menu";
import { css } from "VSSUI/Util";
import { getFriendlyDisplayValue } from "VSSUI/Utilities/FriendlyNumber";
import * as WorkItemContracts from "Wit/Clients/SearchLegacy/Contracts/WorkItem";
import * as LegacyRestClient from "Wit/Clients/SearchLegacy/RestClient/Search";
import * as SearchSharedLegacy from "Wit/Clients/SearchLegacy/Contracts/Search.SharedLegacy";
import { BaseInstantSearchProvider } from "InstantSearch/Shared/InstantSearchProviders/BaseInstantSearchProvider";
import { IExecuteSearchService } from "InstantSearch/Shared/Services/ExecuteSearchService";
import { ResultRow } from "InstantSearch/Shared/ResultRow";
import { TelemetryWriter } from "InstantSearch/Shared/TelemetryWriter";
import { IMenuServiceContext, IInstantSearchMenuService } from "InstantSearch/Shared/Types";
import { WitRef, CustomerIntelligenceConstants } from "Wit/Search/Constants";
import { IDisplayWorkItemService } from "Wit/Search/InstantSearchProviders/Services/DisplayWorkItemService";
import * as Resources from "Wit/Search/Resources";


const maxResultRows = 5;

export interface WorkItemFetchError extends Error {
    activityId: string;
}

/**
 * Service for creating menu items for work items search suggestions.
 */
export class WorkItemSuggestionsProvider extends BaseInstantSearchProvider<SearchSharedLegacy.EntitySearchResponseWithActivityId> implements IInstantSearchMenuService<IMenuServiceContext> {
    protected _contributionId: string = "";

    private _legacySearchClient: LegacyRestClient.ISearchRestClient;
    private _projectProperties: IProjectData | undefined;
    private _providerId: string = "";
    private _projectFilter: string = "";

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._initContribution(this._contributionId);
        this._legacySearchClient = this.pageContext.getRestClient<LegacyRestClient.ISearchRestClient>("Legacy.ISearchRestClient");
    }

    protected _getMenuItems(menuId: string,
        context: IMenuServiceContext,
        invokeProvider: boolean,
        searchText: string
    ): IReadonlyObservableArray<IMenuItem> | null {
        if (invokeProvider && searchText) {
            this.getSuggestions(
                searchText,
                context,
                "WorkItemSuggestionsFetched",
                CustomerIntelligenceConstants.TTISearchScenarioName,
                CustomerIntelligenceConstants.SubsequentSearchScenarioName
            );
        } else {
            this._menuItems.splice(0, this._menuItems.length);
        }

        return this._menuItems;
    }

    protected _initContribution(contributionId: string): void {
        super._initContribution(contributionId);

        if (this._contribution) {
            const { searchProjectFilter, searchProviderId } = this._contribution;
            this._providerId = searchProviderId ? searchProviderId : "";
            this._projectFilter = searchProjectFilter ? searchProjectFilter : "";
        }
    }

    protected _fetchResults(searchText: string, context: IMenuServiceContext): Promise<SearchSharedLegacy.EntitySearchResponseWithActivityId> {
        const projectId = this.projectProperties ? this.projectProperties.id : "";
        const projectName = this.projectProperties ? this.projectProperties.name : "";
        const searchFilters: { [key: string]: string[] } = projectName && this._projectFilter ? { [this._projectFilter]: [projectName] } : {};
        const instantSearchRequest: WorkItemContracts.WorkItemSearchRequest = {
            searchText: searchText,
            skipResults: 0,
            takeResults: maxResultRows,
            summarizedHitCountsNeeded: false,
            includeSuggestions: false,
            searchFilters: searchFilters,
            filters: [],
            sortOptions: [],
            isInstantSearch: true
        };

        return this._legacySearchClient.postWorkItemQuery(instantSearchRequest, projectId);
    }

    protected _onFetchSuccess(
        searchText: string,
        context: IMenuServiceContext,
        data: SearchSharedLegacy.EntitySearchResponseWithActivityId,
        scenario: IVssScenario
    ): void {
        if (data.activityId && data.activityId.length > 0) {
            scenario.addProperties({ responseActivityId: data.activityId[0] });
        }

        const { searchInput, telemetryWriter, onFocus, invokedProvidersProps } = context;
        this._menuItems.splice(0, this._menuItems.length);

        if (this.shouldInvokeProvider(context) && !searchInput.queryText().trim()) {
            // Search text is empty. It got changed before fetching results. So, not updating menuItems.
            return;
        }

        const header = invokedProvidersProps && invokedProvidersProps.props
            ? invokedProvidersProps.props.header
            : Resources.WorkItemsHeader;
        const workItemSuggestionsResponse = data.response as WorkItemContracts.WorkItemSearchResponse;
        const { values, count } = workItemSuggestionsResponse.results;
        const searchResults = values;
        const totalCount = count;

        if (searchResults && searchResults.length > 0) {
            // Add header
            this._menuItems.push({
                id: "",
                groupKey: this._groupKey,
                rank: this._rank,
                itemType: MenuItemType.Header,
                text: header,
                className: "search-suggestion-header"
            });

            // Add search results
            const addFocusOnFirstResult = searchResults.length === 1;
            searchResults.forEach((workItemResult, index) => {
                let fieldMap: { [key: string]: string; } = {};
                workItemResult.fields.map((c: WorkItemContracts.WorkItemField) => { fieldMap[c.referenceName] = c.value; });
                const id = fieldMap[WitRef.id];
                const type = fieldMap[WitRef.type];
                const title = fieldMap[WitRef.title];
                const project = workItemResult.project;

                this._menuItems.push({
                    id: "",
                    className: css(addFocusOnFirstResult && index === 0 && "focused"),
                    groupKey: this._groupKey,
                    rank: this._rank,
                    renderMenuCell: (menuCell: MenuCell, menuItem: IMenuItem, details: IMenuItemDetails): JSX.Element | undefined => {
                        details.onFocusItem = onFocus;
                        switch (menuCell) {
                            case MenuCell.PrimaryText:
                                return <ResultRow
                                    workItemIconProps={{ project: project, workItemType: type }}
                                    title={title}
                                    mutedText={'#' + id}
                                    mutedTextAriaLabel={format(Resources.WorkItemIdAriaLabel, id)}
                                />;
                            default:
                                return undefined;
                        }
                    },
                    onActivate: (menuItem: IMenuItem, event: React.KeyboardEvent<HTMLElement>) =>
                        this._displayWorkItem(
                            id,
                            project,
                            event && event.ctrlKey,
                            searchInput.setHelpVisibility,
                            () => this._recordWorkItemClickTelemetry(index, telemetryWriter))
                });
            });

            // Add row to view all workitem results on search portal
            if (totalCount > searchResults.length) {
                const title = format(Resources.ViewAllResults, getFriendlyDisplayValue(totalCount));

                this._menuItems.push({
                    id: "",
                    groupKey: this._groupKey,
                    rank: this._rank,
                    renderMenuCell: (menuCell: MenuCell, menuItem: IMenuItem, details: IMenuItemDetails): JSX.Element | undefined => {
                        details.onFocusItem = onFocus;
                        switch (menuCell) {
                            case MenuCell.PrimaryText:
                                return <ResultRow iconProps={{ iconName: "Search" }} title={title} />;
                            default:
                                return undefined;
                        }
                    },
                    onActivate: (menuItem: IMenuItem, event: React.KeyboardEvent<HTMLElement>) =>
                        this._executeSearch(
                            searchText,
                            this._providerId,
                            undefined,
                            event.ctrlKey,
                            () => this._onViewAllResultsClick(maxResultRows, searchInput.setHelpVisibility, telemetryWriter))
                });
            }

            announce(searchResults.length === 1 ? Resources.SingleResultLoadedMessage : Resources.ResultsLoadedMessage);
        } else if (searchResults && searchResults.length === 0) {
            const text = this.projectProperties
                ? format(Resources.NoSearchSuggestionsInProjectMessage, Resources.WorkItemText, this.projectProperties.name)
                : format(Resources.NoSearchSuggestionsMessage, Resources.WorkItemText);

            // If this is quick navigation mode then show header as well
            if (invokedProvidersProps) {
                this._menuItems.push({
                    id: "",
                    groupKey: this._groupKey,
                    rank: this._rank,
                    itemType: MenuItemType.Header,
                    text: header,
                    className: "search-suggestion-header"
                });
            }

            // Update message
            this._menuItems.push({
                id: "",
                itemType: MenuItemType.Header,
                className: "suggestion-message",
                groupKey: this._groupKey,
                rank: this._rank,
                text: text
            });

            announce(text);
            telemetryWriter.publish("NoSuggestionsFound", { "type": "InstantSearch" });
        }
    }

    protected _onFetchFail(context: IMenuServiceContext, error: WorkItemFetchError, scenario: IVssScenario): void {
        if (error.activityId) {
            scenario.addProperties({ responseActivityId: error.activityId });
        }

        const { invokedProvidersProps } = context;
        const header = invokedProvidersProps && invokedProvidersProps.props
            ? invokedProvidersProps.props.header
            : Resources.WorkItemsHeader;
        this._menuItems.splice(0, this._menuItems.length);
        this._menuItems.push(
            {
                id: "",
                groupKey: this._groupKey,
                rank: this._rank,
                itemType: MenuItemType.Header,
                text: header,
                className: "search-suggestion-header"
            },
            {
                id: "",
                itemType: MenuItemType.Header,
                className: "suggestion-message",
                groupKey: this._groupKey,
                rank: this._rank,
                text: Resources.GenericErrorMessage
            }
        );

        announce(Resources.GenericErrorMessage);
    }

    private _displayWorkItem = (
        workItemId: string,
        projectName: string,
        openInNewTab?: boolean,
        setHelpVisibility?: (hideHelp: boolean, focusInputOnShow?: boolean) => void,
        callback?: () => void
    ): void => {
        const displayWorkItemService = this.pageContext.getService<IDisplayWorkItemService>("IDisplayWorkItemService");
        displayWorkItemService.displayWorkItem(workItemId, projectName, openInNewTab, setHelpVisibility, callback);
    }

    private _executeSearch = (queryText: string, searchProviderId?: string, entityType?: string, openInNewWindow?: boolean, callbackBeforeExecute?: () => void): void => {
        const executeSearchService = this.pageContext.getService<IExecuteSearchService>("IExecuteSearchService");
        executeSearchService.executeSearch(queryText, searchProviderId, entityType, openInNewWindow, callbackBeforeExecute);
    }

    private _recordWorkItemClickTelemetry = (index: number, telemetryWriter: TelemetryWriter): void => {
        telemetryWriter.publish("ResultClicked", {
            "index": index,
            "resultType": "InstantSearchWorkItem"
        });
    }

    private _onViewAllResultsClick = (index: number, setHelpVisibility: (hideHelp: boolean, focusInputOnShow?: boolean) => void, telemetryWriter: TelemetryWriter): void => {
        // Hide callout
        setHelpVisibility(true);

        // Record telemetry
        telemetryWriter.publish("ResultClicked", {
            "index": index,
            "resultType": "ViewAllWorkItemsLink"
        });
    }

    private get projectProperties(): IProjectData | undefined {
        if (!this._projectProperties) {
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();
            this._projectProperties = tfsPageData ? tfsPageData.project : undefined;
        }

        return this._projectProperties;
    }
}

function getWorkItemSuggestionsProvider(contributionId: string): typeof WorkItemSuggestionsProvider {
    return class extends WorkItemSuggestionsProvider {
        protected _contributionId = contributionId;
    }
}

Services.add(
    "project-workitem-suggestions-provider-service",
    { serviceFactory: getWorkItemSuggestionsProvider("ms.vss-work-web.workitem-suggestions-project-menu-provider") }
);

Services.add(
    "collection-workitem-suggestions-provider-service",
    { serviceFactory: getWorkItemSuggestionsProvider("ms.vss-work-web.workitem-suggestions-collection-menu-provider") }
);
